















































import {
  defineComponent,
  PropType,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from '@vue/composition-api';
import { Form, Button, Dialog } from '@hb/coffee';
import store from '@/store';
import { trimAll } from '@/shared/utils/trim';
import { VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { IS_PRERENDERING } from '@hb/coffee-prerender';
import { useIOSKeyboardScrollLock } from '@/views_v2/verify-credit/useIOSKeyboardScrollLock';
import { SupportComponents } from '@/components/supplement-info-fields/components';
import { InfoComplementField } from '@/components/new-info-complete/types';
import { agreementService } from '@/service/confirm-agreements';
import { twoFactorIdentitySave } from '@/api/verify-credit';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { TP_TWO_FACTOR_SUBMIT_ERROR_POPUP_EXPOSE, TP_TWO_FACTOR_SUBMIT_ERROR_POPUP_CLICK } from '@/views_v2/verify-credit/tianqi';
import platform from '@/shared/utils/platform';
import { convergeAgreementService } from '@/service/confirm-converge-agreements';
import { StateType, TwoFactorFieldEnum } from './types';
import CreditFlowHeader from '../../credit-flow-header';
import { twoFactorStoreActions } from './store';

export default defineComponent({
  name: 'TwoFactor',
  components: {
    CreditFlowHeader,
    Form,
    Button,
  },
  props: {
    /** 当前是否"最后一步" */
    processIsLastVerify: {
      type: Boolean,
      required: false,
      default: false,
    },
    verifyScene: {
      type: String as PropType<VerifySceneEnum>,
      required: false,
    },
    ocrAfterLoan: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['ubtClickSubmit', 'ubtExposeModule'],
  setup(props: any, { emit }: any) {
    // State
    const twoFactor = ref<HTMLElement | null>(null);
    const form = ref<InstanceType<typeof Form> | null>(null);
    const submitInLoading = ref(false);

    // 字段组件的 refs
    const userNameRef = ref<InfoComplementField | null>(null);
    const idCardNoRef = ref<InfoComplementField | null>(null);

    // Store
    const formData = computed<StateType['formData']>(
      () => (store.state as any).twoFactor.formData,
    );

    // 底部按钮文案
    const confirmText = computed(
      () => (props.processIsLastVerify ? '提交' : '下一步 人脸验证'),
    );

    // 动态获取组件
    const userNameComponent = computed(
      () => SupportComponents.getComponent(TwoFactorFieldEnum.USER_NAME),
    );

    const idCardNoComponent = computed(
      () => SupportComponents.getComponent(TwoFactorFieldEnum.ID_CARD_NO),
    );

    const onChangeFormData = (
      propName: keyof Partial<TwoFactorFieldEnum>,
      propValue: string | number,
    ) => {
      twoFactorStoreActions.updateFormData({
        ...formData.value,
        [propName]: propValue,
      });
    };

    const onUserNameChange = (value: any) => {
      onChangeFormData(TwoFactorFieldEnum.USER_NAME as any, value);
    };

    const onIdCardNoChange = (value: any) => {
      onChangeFormData(TwoFactorFieldEnum.ID_CARD_NO as any, value);
    };

    // iOS 有输入键盘时禁止页面滚动
    const iosKeyboardScrollLock = useIOSKeyboardScrollLock();

    /** 检验二要素表单输入是否通过 */
    const formValidator = async (): Promise<boolean> => {
      let validateResult = true;

      try {
        if (Boolean(await userNameRef.value?.onValidator()) === false) {
          validateResult = false;
        }

        if (Boolean(await idCardNoRef.value?.onValidator()) === false) {
          validateResult = false;
        }
      } catch (error) {
        ubtSendError({
          name: 'two-factor-form-validator-error',
          message: (error as Error)?.message,
        });
        validateResult = false;
      }

      return validateResult;
    };

    /** 聚焦到 userName 输入框 */
    const focusUserNameInput = (): void => {
      if (userNameRef.value?.$el) {
        const inputElement = userNameRef.value.$el.querySelector('input');

        if (inputElement && typeof inputElement.focus === 'function') {
          inputElement.focus();
        }
      }
    };

    /** 提交 */
    const onSubmit = async (): Promise<void> => {
      emit('ubtClickSubmit');

      /** 若二要素表单校验不通过，则终止提交 */
      if (!await formValidator()) return;
      /** 校验是否用户注册协议 */
      if (!props.ocrAfterLoan && !(await agreementService.confirmFinanceAgreements())) return;
      /** 若正在提交loading中，则结束此次提交流程 */
      if (submitInLoading.value) return;

      submitInLoading.value = true;

      const { userName, idCardNo } = formData.value;

      try {
        await twoFactorIdentitySave(
          {
            userName: trimAll(userName),
            idCardNo: trimAll(idCardNo),
          },
          {
            showErrMsg: (msg: string, error: unknown) => {
              const { code } = (error as { code: string });

              // 姓名和身份证号不匹配，曝光弹窗曝光
              ubtSetupModuleExpose(TP_TWO_FACTOR_SUBMIT_ERROR_POPUP_EXPOSE, { code, msg });

              Dialog.confirm({
                title: msg || '抱歉，您的姓名与身份证号不一致，请修改信息后重试',
                confirmButtonText: '修改信息',
                showCancelButton: false,
                closeable: true,
              }).then(() => {
                ubtSetupPageButton(TP_TWO_FACTOR_SUBMIT_ERROR_POPUP_CLICK, { code, msg });
                focusUserNameInput();
              });
            },
          },
        );

        /** ocr后置流程 && 无二要素用户，二要素保存成功之后，才可以签署征信协议，否则流程异常 */
        if (props.ocrAfterLoan && !(await convergeAgreementService.confirmConvergeAgreements())) {
          return;
        }

        emit('resolveProcessVerify');
      } catch (error) {
        ubtSendError({
          name: 'two-factor-submit-error',
          message: (error as { code: string })?.code,
        });
      } finally {
        submitInLoading.value = false;
      }
    };

    onMounted(() => {
      if (!IS_PRERENDERING) {
        emit('ubtExposeModule');
        /** 自动聚焦到姓名输入框: ios不支持 */
        nextTick(() => {
          if (!platform.isIOS) {
            focusUserNameInput();
          }
        });
      }
    });

    onBeforeUnmount(() => {
      /** 组件卸载时，清理限制页面滚动的事件监听 */
      iosKeyboardScrollLock.cleanup();
    });

    return {
      twoFactor,
      form,
      formData,
      confirmText,
      userNameComponent,
      idCardNoComponent,
      userNameRef,
      idCardNoRef,
      TwoFactorFieldEnum,
      onUserNameChange,
      onIdCardNoChange,
      onChangeFormData,
      onSubmit,
      iosKeyboardScrollLock,
    };
  },
});
